import React, { Fragment, FunctionComponent, useMemo, useState } from "react";
import {
  Box,
  Chip,
  InputAdornment,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import { adminLinkGroups, AdminLink } from "./adminLinks";

const hostOf = (url: string) => {
  try {
    const u = new URL(url);
    return u.host + (u.pathname !== "/" ? u.pathname : "");
  } catch {
    return url;
  }
};

const matches = (l: AdminLink, q: string) =>
  [l.name, l.url, l.note ?? ""].some((s) => s.toLowerCase().includes(q));

const open = (url: string) => window.open(url, "_blank", "noopener,noreferrer");

export const AdminLinksTable: FunctionComponent = () => {
  const [query, setQuery] = useState("");

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return adminLinkGroups;
    return adminLinkGroups
      .map((g) => ({ ...g, links: g.links.filter((l) => matches(l, q)) }))
      .filter((g) => g.links.length > 0);
  }, [query]);

  return (
    <Paper elevation={4} sx={{ borderRadius: 3, boxShadow: 6, mb: 3, p: 2 }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search app, host or note…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <TableContainer>
        <Table size="small" aria-label="admin apps table">
          <TableHead>
            <TableRow sx={{ backgroundColor: (theme) => theme.palette.primary.main }}>
              <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: 15 }}>APP</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: 15 }}>Host</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: 15, width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((g) => (
              <Fragment key={g.title}>
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{
                      fontWeight: 700,
                      color: "secondary.main",
                      backgroundColor: "action.hover",
                      textTransform: "uppercase",
                      fontSize: 12,
                      letterSpacing: 0.8,
                    }}
                  >
                    {g.title}
                  </TableCell>
                </TableRow>
                {g.links.map((l) => (
                  <TableRow
                    hover
                    key={l.name}
                    onClick={() => open(l.url)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, m: "0 !important" }}>
                          {l.name}
                        </Typography>
                        {l.verify && (
                          <Tooltip title="Link not confirmed yet" arrow>
                            <Chip label="verify" size="small" color="warning" variant="outlined" />
                          </Tooltip>
                        )}
                      </Box>
                      {l.note && (
                        <Typography variant="caption" color="text.secondary" sx={{ m: "0 !important" }}>
                          {l.note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}>
                      {hostOf(l.url)}
                    </TableCell>
                    <TableCell>
                      <Link
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Open ${l.name}`}
                      >
                        <OpenInNewIcon color="primary" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
            {groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} sx={{ color: "text.secondary" }}>
                  No match
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
